import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdSchedule, MdSend, MdArrowBack } from 'react-icons/md';
import ScheduleForm from './ScheduleForm';
import PostNowForm from './PostNowForm';
import type { ShareModalProps, ShareOption, ScheduleData, SocialMedium } from './types';

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  flyerImageUrl,
  generatedContentId,
  flyerId,
  socialMediaData,
  restaurantId,
  onPostNow,
  onSchedule
}) => {
  const [selectedOption, setSelectedOption] = useState<ShareOption | null>(null);

  const handleOptionSelect = (option: ShareOption) => {
    setSelectedOption(option);
  };

  const handlePostNowSubmit = (selectedPlatforms: SocialMedium[]) => {
    console.log('Posting to platforms:', selectedPlatforms);
    if (onPostNow) {
      onPostNow();
    }
    onClose();
    setSelectedOption(null);
  };

  const handleScheduleSubmit = (data: ScheduleData) => {
    if (onSchedule) {
      onSchedule(data);
    }
    onClose();
    setSelectedOption(null);
  };

  const handleBack = () => {
    setSelectedOption(null);
  };

  const handleClose = () => {
    setSelectedOption(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              style={{
                backdropFilter: 'blur(10px)',
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
                background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
                border: '1px solid rgba(224, 224, 224, 0.13)'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
                <div className="flex items-center gap-3">
                  {selectedOption && (
                    <button
                      onClick={handleBack}
                      className="text-[#EE4423] hover:text-[#d63d20] transition-colors"
                    >
                      <MdArrowBack size={24} />
                    </button>
                  )}
                  <h2 className="text-2xl font-bold text-[#EE4423]">
                    {selectedOption === 'schedule-later' ? 'Schedule Campaign' : selectedOption === 'post-now' ? 'Post Now' : 'Share Campaign'}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  className="text-[#EE4423] hover:text-[#d63d20] transition-colors"
                >
                  <MdClose size={28} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {!selectedOption ? (
                  // Option Selection View
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Post Now Option */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect('post-now')}
                      className="group relative overflow-hidden rounded-xl transition-all duration-300 p-8 bg-[#212121]"
                      style={{border: "1px solid rgba(240, 238, 238, 0.13)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-[#EE4423]/10 flex items-center justify-center group-hover:bg-[#EE4423]/20 transition-colors">
                          <MdSend size={32} className="text-[#EE4423]" />
                        </div>
                        <h3 className="text-xl font-semibold text-white">Post Now</h3>
                        <p className="text-gray-400 text-sm">
                          Share your campaign immediately to all selected platforms
                        </p>
                      </div>
                    </motion.button>

                    {/* Schedule Later Option */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect('schedule-later')}
                      
                      className="group relative overflow-hidden rounded-xl transition-all duration-300 p-8 bg-[#212121]"
                      style={{border: "1px solid rgba(240, 238, 238, 0.13)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-[#EE4423]/10 flex items-center justify-center group-hover:bg-[#EE4423]/20 transition-colors">
                          <MdSchedule size={32} className="text-[#EE4423]" />
                        </div>
                        <h3 className="text-xl font-semibold text-white">Schedule Later</h3>
                        <p className="text-gray-400 text-sm">
                          Plan and schedule your campaign for a specific date and time
                        </p>
                      </div>
                    </motion.button>
                  </div>
                ) : selectedOption === 'post-now' ? (
                  // Post Now Form View
                  <PostNowForm
                    flyerImageUrl={flyerImageUrl}
                    generatedContentId={generatedContentId}
                    flyerId={flyerId}
                    socialMediaData={socialMediaData}
                    restaurantId={restaurantId}
                    onSubmit={handlePostNowSubmit}
                  />
                ) : (
                  // Schedule Form View
                  <ScheduleForm
                    flyerImageUrl={flyerImageUrl}
                    generatedContentId={generatedContentId}
                    flyerId={flyerId}
                    socialMediaData={socialMediaData}
                    restaurantId={restaurantId}
                    onSubmit={handleScheduleSubmit}
                  />
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
